import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Overline, OrnamentDivider } from "@/components/GoldDivider";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BUSINESS, PROJECT_TYPES, REFERRAL_SOURCES } from "@/data/content";

const EMPTY = { name: "", phone: "", email: "", project_type: "", referral_source: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.project_type) {
      toast.error("Please complete name, phone, email and project type.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Request received. We will reach out shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="velvet-deep">
      <section className="pt-36 pb-12 px-6" data-testid="contact-hero">
        <div className="max-w-5xl mx-auto">
          <Overline>Contact</Overline>
          <OrnamentDivider className="mt-6 !justify-start" width={200} />
          <h1 className="font-serif text-5xl sm:text-6xl font-light text-ivory mt-5 leading-[1.05]">
            Let us build something worth keeping.
          </h1>
        </div>
      </section>

      <section className="pb-24 px-6" data-testid="contact-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14">
          <div>
            <p className="text-[#A9B7CE] text-lg leading-relaxed">
              Reach out and we will schedule your free 3D design consultation. We respond to every inquiry within a few hours.
            </p>
            <div className="space-y-5 mt-10">
              <Row icon={Phone} label="Call or text"><a href={BUSINESS.phoneHref} className="hover:text-gold transition-colors" data-testid="contact-phone-link">{BUSINESS.phone}</a></Row>
              <Row icon={Mail} label="Email"><a href={`mailto:${BUSINESS.email}`} className="hover:text-gold transition-colors">{BUSINESS.email}</a></Row>
              <Row icon={MapPin} label="Shop">{BUSINESS.addressLine}</Row>
              <Row icon={Clock} label="Hours">{BUSINESS.hours}</Row>
            </div>
            <div className="mt-10 border border-gold/20">
              <iframe title="West Ouachita Cabinets location" src={BUSINESS.mapEmbed}
                className="w-full h-64 grayscale contrast-125" loading="lazy" data-testid="contact-map" />
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="bg-navy-surface border border-gold/25 p-8 sm:p-10" data-testid="contact-form-card">
            {done ? (
              <div className="h-full flex flex-col items-center justify-center text-center gap-5 py-10" data-testid="contact-success">
                <CheckCircle2 className="h-14 w-14 text-gold" strokeWidth={1} />
                <h3 className="font-serif text-3xl text-ivory">Thank You</h3>
                <p className="text-[#A9B7CE] leading-relaxed max-w-sm">
                  Your request is in. We will reach out within a few hours to schedule your free 3D design consultation.
                </p>
                <button onClick={() => setDone(false)} className="text-gold text-sm tracking-wider uppercase hover:text-gold-light" data-testid="contact-reset">Send another request</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <h3 className="font-serif text-3xl text-ivory mb-2">Request Your Consultation</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name" required><Input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none bg-navy border-gold/25 text-ivory focus-visible:ring-gold" placeholder="Your name" /></Field>
                  <Field label="Phone" required><Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-none bg-navy border-gold/25 text-ivory focus-visible:ring-gold" placeholder="(318) 000-0000" /></Field>
                </div>
                <Field label="Email" required><Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none bg-navy border-gold/25 text-ivory focus-visible:ring-gold" placeholder="you@email.com" /></Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Project Type" required>
                    <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
                      <SelectTrigger data-testid="contact-project-type" className="rounded-none bg-navy border-gold/25 text-ivory focus:ring-gold"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="rounded-none bg-navy border-gold/25 text-ivory">
                        {PROJECT_TYPES.map((t) => <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-ivory">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="How did you hear of us?">
                    <Select value={form.referral_source} onValueChange={(v) => set("referral_source", v)}>
                      <SelectTrigger data-testid="contact-referral" className="rounded-none bg-navy border-gold/25 text-ivory focus:ring-gold"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="rounded-none bg-navy border-gold/25 text-ivory">
                        {REFERRAL_SOURCES.map((t) => <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-ivory">{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Tell us about your project"><Textarea data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} className="rounded-none bg-navy border-gold/25 text-ivory focus-visible:ring-gold min-h-[100px]" placeholder="Optional" /></Field>
                <button data-testid="contact-submit" type="submit" disabled={loading}
                  className="w-full bg-gold text-navy-deep font-semibold tracking-luxe uppercase text-sm px-8 py-4 rounded-none hover:bg-gold-light transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}{loading ? "Sending" : "Request My Consultation"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-4">
      <Icon className="h-5 w-5 text-gold shrink-0 mt-1" strokeWidth={1.5} />
      <div>
        <p className="text-[#66768c] text-xs tracking-luxe uppercase">{label}</p>
        <p className="text-ivory mt-0.5">{children}</p>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#A9B7CE] text-xs tracking-wider uppercase">{label} {required && <span className="text-gold">*</span>}</Label>
      {children}
    </div>
  );
}
